import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient, uniqueDeviceId } from '../api/http';
import { ApiResponse, LoginResponse } from '../types';
import { encryptData, decryptData, loginEncyptionKey } from '../helpers/utils/crypto-js';
import { purgeCredentials } from '../helpers/utils/auth-payload';
import { localStorageEnum, sessionStorageEnum } from '../api/enums';
import { Alert } from '@piggytechng/component-library';

const useAuth = () => {
  const [loginForm, setLoginForm] = useState({
    identifier: '',
    password: '',
  });

  const [otpForm, setOtpForm] = useState({
    otp: '',
  });

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const [updatePasswordForm, setUpdatePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [fetchedEncryptionKey, setFetchedEncryptionKey] = useState<string>();

  const [qrCodeImg, setQrCodeImg] = useState<string>();
  const [activeScreen, setActiveScreen] = useState<string>('login');

  const handleUpdatePasswordForm = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    setUpdatePasswordForm({ ...updatePasswordForm, [name]: value });
  };

  const handleLoginForm = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    setLoginForm({ ...loginForm, [name]: value });
  };

  const handleOtpForm = (val: string) => {
    setOtpForm({ ...otpForm, otp: val });
  };

  const [twoFaResetId, setTwoFaResetId] = useState<string>();

  const login = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const url = '/auth/login';

      let payload: any = {
        identifier: undefined,
        password: undefined,
        admin: undefined,
      };
      const passwordEncrypted = encryptData(loginForm.password, loginEncyptionKey);

      if (activeScreen === 'login') {
        payload = {
          identifier: encryptData(loginForm.identifier, loginEncyptionKey!),
          password: encryptData(loginForm.password, loginEncyptionKey!),
          admin: true,
        };
      } else {
        payload = {
          identifier: encryptData(loginForm.identifier, loginEncyptionKey!),
          password: passwordEncrypted,
          admin: true,
          adminTotp: encryptData(otpForm.otp, loginEncyptionKey!),
        };
      }
      const response: LoginResponse = await httpClient.post(url, payload);

      if (response.data.status) {
        const { admin, userReference, accessToken } = response.data.data;
        const { twoFaEnabled, twoFaSetup, message, email, firstName, lastName } = admin;
        Alert.info(message);

        const expireTime = Date.now() + 2 * 60 * 60 * 1000; // 2 hours in milliseconds
        localStorage.setItem(localStorageEnum['local.storage.userId'], userReference);

        if (accessToken) {
          // localStorage.setItem(localStorageEnum['local.storage.userId'], userReference);
          localStorage.setItem(localStorageEnum['local.storage.email'], email);
          localStorage.setItem(localStorageEnum['local.storage.firstName'], firstName);
          localStorage.setItem(localStorageEnum['local.storage.lastName'], lastName);
          sessionStorage.setItem(sessionStorageEnum['session.storage.token'], accessToken);
          localStorage.setItem(localStorageEnum['local.storage.sessionExpiryTime'], expireTime.toString());
          localStorage.setItem('userRoleID', admin.roleId ?? '');
          localStorage.setItem('isLoggedIn', '1');
          purgeCredentials();
          navigate('/');
          return;
        }

        if (twoFaEnabled) {
          setActiveScreen('otp-input-screen');
          return;
        } else {
          setQrCodeImg(twoFaSetup?.qrCode);
          setActiveScreen('2fa-setup-screen');
          return;
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    const { currentPassword, newPassword, confirmPassword } = updatePasswordForm;

    try {
      if (newPassword !== confirmPassword) {
        Alert.warn('New password and confirm password do not match');
        throw new Error('New password and confirm password is different');
      }

      if (currentPassword === newPassword) {
        Alert.warn('New password must be different from the old password');
        throw new Error('Old password is the same as new password');
      }

      if (newPassword.length < 6) {
        Alert.warn('Password must have at least 6 characters');
        throw new Error('Password must have at least 6 characters');
      }

      const decyptionKey = `${localStorage.getItem(localStorageEnum['local.storage.userId'])}:${uniqueDeviceId}`;
      const encryptionKey = decryptData(fetchedEncryptionKey!, decyptionKey);

      const payload = {
        currentPassword: encryptData(currentPassword, encryptionKey),
        newPassword: encryptData(newPassword, encryptionKey),
        confirmPassword: encryptData(newPassword, encryptionKey),
      };

      const response = await httpClient.post('/admin/change-password', payload);
      if (response) {
        Alert.success('Password updated successfully');
        logout();
      }
    } catch (error) {
      console.log(error);
      return error;
    }
  };

  const fetchEncryptionKey = async () => {
    try {
      setIsLoading(true);
      const url = 'admin/request-encryption/';
      const response: ApiResponse = await httpClient.post(url, { encryptionKey: 'general' });
      const { status, data } = response.data;
      if (status) {
        setFetchedEncryptionKey(data.key);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const request2FaResetPin = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const userReference = localStorage.getItem(localStorageEnum['local.storage.userId']);
      const payload = {
        identifier: encryptData(loginForm.identifier, loginEncyptionKey!),
        password: encryptData(loginForm.password, loginEncyptionKey!),
        userId: userReference,
      };
      const url = 'admin/2fa-reset/request-pin';
      const response: ApiResponse = await httpClient.post(url, payload);

      const { status, data } = response.data;
      if (status) {
        setTwoFaResetId(data.verificationId);
        setActiveScreen('2fa-reset-verification');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const verify2FaResetPin = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const userReference = localStorage.getItem(localStorageEnum['local.storage.userId']);

      const payload = {
        userId: userReference,
        verificationId: twoFaResetId,
        resetPin: otpForm.otp,
      };
      const url = 'admin/2fa-reset/verify-pin';
      const response: ApiResponse = await httpClient.post(url, payload);
      const { status, data } = response.data;
      if (status) {
        setQrCodeImg(data.qrCode);
        setOtpForm({
          otp: '',
        });
        setActiveScreen('2fa-setup-screen');
        return;
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
  };

  return {
    handleLoginForm,
    login,
    loginForm,
    setLoginForm,
    handleOtpForm,
    otpForm,
    logout,
    handleUpdatePasswordForm,
    updatePassword,
    isLoading,
    fetchEncryptionKey,
    request2FaResetPin,
    verify2FaResetPin,
    activeScreen,
    setActiveScreen,
    qrCodeImg,
  };
};

export default useAuth;
